import { APIClient, ApiQueryParams, DefaultQueryParams } from '@frontend/api-utils';
import type { Token } from '@frontend/authentication-v2';
import { CreateDeceased, DeceasedListResponse, DeceasedQueryParams, IDeceased, UpdateDeceased } from '@frontend/deceased/types';

export class DeceasedClient extends APIClient {
    public static readonly endpoint = '/deceased-api/v1';

    public static async fetchDeceaseds(queryParams?: ApiQueryParams<DeceasedQueryParams>): Promise<DeceasedListResponse> {
        return await this.apiPaginated<DeceasedListResponse, DeceasedQueryParams>(`${this.endpoint}/deceaseds`, queryParams);
    }

    /** Personal (“my”) deceaseds — only those linked to nivaliz transactions where the auth user is sender or receiver. */
    public static async fetchMyDeceaseds(queryParams?: ApiQueryParams<DeceasedQueryParams>): Promise<DeceasedListResponse> {
        return await this.apiPaginated<DeceasedListResponse, DeceasedQueryParams>('/deceased-api/ext/v1/my-deceaseds', queryParams);
    }

    public static async fetchDeceasedsNotInGroup(
        accountId: string,
        groupId: string,
        queryParams?: ApiQueryParams<DefaultQueryParams>
    ): Promise<DeceasedListResponse> {
        return await this.apiPaginated<DeceasedListResponse, DefaultQueryParams>(
            `${this.endpoint}/accounts/${accountId}/groups/${groupId}/deceaseds`,
            queryParams
        );
    }

    public static async resolveDeceaseds(ids: string[], field: string = 'id'): Promise<DeceasedListResponse> {
        return this.resolve<DeceasedListResponse>(`${this.endpoint}/deceaseds`, ids, field);
    }

    public static async deleteDeceaseds(ids: string[]): Promise<void> {
        const response = await this.post(`${this.endpoint}/deceaseds-delete`, { ids });
        return await this.handleVoidResponse(response);
    }

    public static async postDeceased(accountId: string, deceased: CreateDeceased, withCredentials?: Token): Promise<IDeceased> {
        const response = await this.post(`${this.endpoint}/accounts/${accountId}/deceaseds`, deceased, true, withCredentials);
        return await this.handleResponse<IDeceased>(response);
    }

    public static async fetchDeceased(accountId: string, deceasedId: string): Promise<IDeceased> {
        const response = await this.fetch(`${this.endpoint}/accounts/${accountId}/deceaseds/${deceasedId}`);
        return await this.handleResponse<IDeceased>(response);
    }

    public static async patchDeceased(accountId: string, deceasedId: string, deceased: UpdateDeceased): Promise<IDeceased> {
        const response = await this.patch(`${this.endpoint}/accounts/${accountId}/deceaseds/${deceasedId}`, deceased);
        return await this.handleResponse<IDeceased>(response);
    }

    public static async deleteDeceased(accountId: string, deceasedId: string): Promise<void> {
        const response = await this.delete(`${this.endpoint}/accounts/${accountId}/deceaseds/${deceasedId}`);
        return await this.handleVoidResponse(response);
    }
}
